import { useState } from 'react'

/**
 * Shared Granularity for the admin dashboard + partner-detail overview chart.
 *
 * The admin reports page has a wider union including 'yearly' — that variant
 * stays local to reports-page; this module owns only the 3-value union the
 * dashboard and partner-detail overview both use, so the dedup doesn't force
 * a broken merge.
 */
export type Granularity = 'daily' | 'weekly' | 'monthly'

export const GRANULARITIES = ['daily', 'weekly', 'monthly'] as const

export const isGranularity = (v: string): v is Granularity =>
  (GRANULARITIES as readonly string[]).includes(v)

/**
 * Granularity-driven date window:
 *   daily   → last 30 days
 *   weekly  → last 90 days
 *   monthly → last 365 days
 *
 * `date_from` / `date_to` are ISO date strings (YYYY-MM-DD) so the BE can
 * compute the period and delta_pct fields it only emits when both are given.
 */
export const getDateRange = (granularity: Granularity): { date_from: string; date_to: string } => {
  const now = new Date()
  const to = now.toISOString().slice(0, 10)
  const from = new Date(now)

  if (granularity === 'daily') {
    from.setDate(from.getDate() - 30)
  } else if (granularity === 'weekly') {
    from.setDate(from.getDate() - 90)
  } else {
    from.setFullYear(from.getFullYear() - 1)
  }

  return { date_from: from.toISOString().slice(0, 10), date_to: to }
}

/**
 * Snapshot the (granularity, dateRange) pair into state so `getDateRange()` —
 * which calls `new Date()` — runs only on toggle, not every render. Without
 * this, `dateRange` is recomputed every render and the query key changes on
 * every tick of the wall clock, defeating TanStack Query's cache identity.
 *
 * The setter replaces the granularity AND its derived range together — the
 * single chokepoint that keeps the two fields consistent.
 */
export const useGranularitySnapshot = (
  initial: Granularity,
): {
  granularity: Granularity
  dateRange: { date_from: string; date_to: string }
  setGranularity: (next: Granularity) => void
} => {
  const [state, setState] = useState(() => ({
    granularity: initial,
    dateRange: getDateRange(initial),
  }))

  const setGranularity = (next: Granularity) => {
    setState({ granularity: next, dateRange: getDateRange(next) })
  }

  return { granularity: state.granularity, dateRange: state.dateRange, setGranularity }
}
